import { type PortalMessage, resolvePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `Organization` → Archive and close — `/organization/archive-close` (plan-014 B4; design §27.15.1; FR12-WSP-03/04/05;
 * AC12-05). Drawn to the prototype's `lifecycle` page (`design/WeTalk.dc.html`, the Organization block renderer):
 * kicker ("… · workspaces and organization"), "Archive, restore, close", the lede, the tab strip, then —
 *
 *   Before archiving <workspace>   the item-by-item preflight (Now / On archive), "Archive when calls finish"; the
 *                                  workspace is chosen in the block head and kept in the URL (`?workspace=`); while it
 *                                  drains, "Cancel archiving" (numbers back; keys and campaigns stay off — said so)
 *   Archived                       each archived workspace with "since <date>" and Restore ("Restoring checks sign-in
 *                                  rules, funding and plan limits again. Campaigns and keys stay off.")
 *   Close the organization         the notice ("Only the owner can close it, with their password …"), "Check what blocks
 *                                  closing" (each blocker in plain words, or "Nothing blocks closing"), then the close
 *                                  dialog: unused purchased credit asked back or waived in writing, the retention
 *                                  stated (content deleted N days after access ends, records kept N years), a fresh
 *                                  confirmation of identity; afterwards the closure's durable status, Withdraw while
 *                                  allowed
 *   Organizations you closed       the person's own closures still in their grace, each with Undo
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04) and decided by the API; the page offers an act only where the role can hold
 * it (`account.workspace.manage` to archive and restore, `account.close` — the Owner — to close). Styles: the shared
 * renderer's (`../page/organization-page.css`), the preflight table's (`../../workspaces/archive-preflight.css`) and
 * `./account-lifecycle.css`, tokens only (ADR 0018).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Field, Radio, Select, Textarea } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../../../api/query.js";
import type {
  AccountClosureStateView,
  AccountClosureView,
  ClosureBlockerView,
  ClosurePreflightView,
  WorkspaceLifecycleRowView,
} from "../../../api/slice/lifecycle.js";
import { GLYPH } from "../../../shell/glyph.js";
import { useClosedOrganizationNotice } from "../../../scope/guard.js";
import { ArchivePreflightDialog, ArchivePreflightTable, archivePreflightKey } from "../../workspaces/archive-preflight.js";
import { roleHolds, useOpenAccount } from "../overview/account-overview-screen.js";
import { Block, BlockNote, ItemColumn, ItemRow, Notice, OrganizationPage, type Outcome } from "../page/organization-page.js";
import { shortDateText } from "../page/role-copy.js";
import { ConfirmIdentityDialog, isStepUpRequired, stepUpReturnOf, stepUpReturnOutcome, type PendingConfirmation } from "../security/confirm-identity.js";
import { MY_ACCOUNT_CLOSURE_KEY } from "../../../shell/former-owner-closure.js";

import "../security/account-security.css";
import "./account-lifecycle.css";

/** The page's search parameters: which workspace the archive preflight is about. */
export interface AccountLifecycleSearch {
  readonly workspace?: string;
  /** Only on the way back from a Google or Microsoft confirmation. */
  readonly step_up?: "confirmed" | "cancelled" | "failed";
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page (an id that is not a UUID is dropped).
 */
export function accountLifecycleSearchOf(raw: Readonly<Record<string, unknown>>): AccountLifecycleSearch {
  const workspace = raw["workspace"];
  return {
    ...(typeof workspace === "string" && UUID.test(workspace.toLowerCase()) ? { workspace: workspace.toLowerCase() } : {}),
    ...stepUpReturnOf(raw),
  };
}

export interface AccountLifecycleScreenProps {
  readonly search: AccountLifecycleSearch;
  readonly onSearch: (next: AccountLifecycleSearch) => void;
}

export const workspaceLifecycleKey = (accountId: string) => ["workspace-lifecycle", "overview", accountId] as const;
export const closurePreflightKey = (accountId: string) => ["account-closure", "preflight", accountId] as const;
// The shell's chooser reads the same entry (`MY_ACCOUNT_CLOSURE_KEY`, INT): an undo in either place refreshes both.
const myClosureKey = MY_ACCOUNT_CLOSURE_KEY;

// ── the words ─────────────────────────────────────────────────────────────────────────────────────────────

function LONG_DATE(): Intl.DateTimeFormat { return new Intl.DateTimeFormat(portalLocale() === "en" ? "en-GB" : "el-GR", { day: "numeric", month: "long", year: "numeric" }); }

/** "€120.00" — EUR at launch (ADR 0014). */
export function euroText(amountMinor: number): string {
  const whole = Math.floor(Math.abs(amountMinor) / 100);
  const cents = String(Math.abs(amountMinor) % 100).padStart(2, "0");
  return `${amountMinor < 0 ? "−" : ""}€${String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${cents}`;
}

/** The answer to "Check what blocks closing" — the prototype's one sentence, built from the stored blockers. */
export function blockerSummaryText(blocker: readonly ClosureBlockerView[]): string {
  if (blocker.length === 0) return portalText("core", "account_admin.nothing_blocks_closing_0738b5");
  return blocker.map((one) => one.detail).join(" ");
}

/** What a closure in each state is doing, in the console's words. */
export function closureStatusText(closure: AccountClosureView): string {
  const until = closure.graceUntil === null ? null : LONG_DATE().format(new Date(closure.graceUntil));
  const words: Readonly<Record<AccountClosureStateView, string>> = {
    preflight_blocked: portalText("core", "account_admin.closing_was_blocked_4f5dec", { value0: blockerSummaryText(closure.blocker) }),
    draining: portalText("core", "account_admin.closing_no_new_calls_or_purchases_waiting_for_the_last_conversati_fdb89e"),
    refunding: portalText("core", "account_admin.closing_waiting_for_a_wetalk_finance_person_to_decide_the_refund_2cf552"),
    cancelling_commercial: portalText("core", "account_admin.closing_ending_your_plan_and_payment_terms_no_plan_value_is_refun_ede2fd"),
    revoking_access: portalText("core", "account_admin.closing_ending_everyone_s_access_to_this_organization_56f85c"),
    grace: portalText("core", "account_admin.access_has_ended_conversations_recordings_and_documents_are_kept_ac0147", { value0: until ?? portalText("core", "account_admin.the_end_of_the_grace_period_d29225") }),
    purging_content: portalText("core", "account_admin.deleting_conversations_recordings_and_documents_invoices_and_the_1d7046"),
    closed: portalText("core", "account_admin.closed_invoices_and_the_ledger_are_kept_until_2f04cf", { value0: closure.financialRetainUntil === null ? portalText("core", "account_admin.the_end_of_the_legal_period_9c0a4b") : LONG_DATE().format(new Date(closure.financialRetainUntil)) }),
    withdrawn: portalText("core", "account_admin.closing_was_withdrawn_start_campaigns_and_automatic_top_ups_again_763cc7"),
    undone: portalText("core", "account_admin.closing_was_undone_invite_your_people_back_reconnect_numbers_and_b889d4"),
  };
  return words[closure.state];
}

/** Whether a closure can still be withdrawn (until access is being revoked, §27.4). */
export function closureWithdrawable(state: AccountClosureStateView): boolean {
  return state === "draining" || state === "refunding" || state === "cancelling_commercial";
}

/** Whether the closure is still moving (the page shows its status rather than the close button). */
function closureOpen(closure: AccountClosureView | null): closure is AccountClosureView {
  return closure !== null && ["draining", "refunding", "cancelling_commercial", "revoking_access", "grace", "purging_content"].includes(closure.state);
}

/** Whether access to the organization is still ending (the page keeps reading the closure until it has ended). */
export function closureEndingAccess(state: AccountClosureStateView): boolean {
  return state === "draining" || state === "refunding" || state === "cancelling_commercial" || state === "revoking_access";
}

/** Whether a closure has ended everyone's access (its grace, the purge, closed) — never a withdrawn or undone one. */
export function closureAccessEnded(state: AccountClosureStateView): boolean {
  return state === "grace" || state === "purging_content" || state === "closed";
}

/** How often the page reads a closure whose access is still ending — the worker moves it every minute. */
export const CLOSURE_STATUS_EVERY_MS = 5_000;

const DAY_MS = 86_400_000;

/** The last day it can be reopened, as it will be: content kept `contentPurgeAfterDay` days after access ends. */
export function reopenUntilOf(closure: AccountClosureView, contentPurgeAfterDay: number): string {
  if (closure.graceUntil !== null) return closure.graceUntil;
  return new Date(Date.parse(closure.requestedAt) + contentPurgeAfterDay * DAY_MS).toISOString();
}

/**
 * 2026-10-15 (owner report: "should it just show me that the organization will close after a few minutes"): what the
 * Owner reads while their organization closes — when it closes, what then happens to everyone's access and to them,
 * how long the content is kept and where it can be reopened from.
 */
export function closingExplanationText(closure: AccountClosureView, contentPurgeAfterDay: number): string {
  if (closure.state === "refunding") {
    return portalText("core", "account_admin.it_closes_once_a_wetalk_finance_person_has_decided_the_refund_5be0a2", { value0: String(contentPurgeAfterDay) });
  }
  return portalText("core", "account_admin.it_will_be_closed_within_a_few_minutes_then_access_ends_for_ever_8d24e1", {
    value0: String(contentPurgeAfterDay),
    value1: LONG_DATE().format(new Date(reopenUntilOf(closure, contentPurgeAfterDay))),
  });
}

/** Whether a read of the closing organization was refused because access to it has ended (never a network fault). */
function accessEndedBy(raised: unknown): boolean {
  return raised instanceof ApiError && (raised.status === 401 || raised.status === 403 || raised.status === 404);
}

/** The archive preflight's subject: the URL's workspace when it can be archived, else the first that can. */
export function preflightWorkspaceOf(rows: readonly WorkspaceLifecycleRowView[], wanted: string | undefined): WorkspaceLifecycleRowView | null {
  const open = rows.filter((one) => one.state !== "archived");
  return open.find((one) => one.workspaceId === wanted) ?? open.find((one) => one.state === "draining") ?? open[open.length - 1] ?? null;
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────

export function AccountLifecycleScreen(props: AccountLifecycleScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canManage = roleHolds(open.accountRole, "account.workspace.manage");
  const canClose = roleHolds(open.accountRole, "account.close");
  // A Google or Microsoft confirmation's return (`?step_up=`) is shown once, then dropped from the address.
  const [outcome, setOutcome] = useState<Outcome | null>(() => stepUpReturnOutcome(props.search.step_up));
  const { onSearch } = props;
  const returned = props.search.step_up;
  const keptWorkspace = props.search.workspace;
  useEffect(() => {
    if (returned !== undefined) onSearch(keptWorkspace === undefined ? {} : { workspace: keptWorkspace });
  }, [returned, keptWorkspace, onSearch]);

  const overview = useQuery({
    queryKey: workspaceLifecycleKey(accountId ?? "none"),
    queryFn: () => gateway.readWorkspaceLifecycle(accountId ?? ""),
    enabled: accountId !== null && roleHolds(open.accountRole, "account.workspace_metadata.read"),
    retry: false,
  });
  const mine = useQuery({ queryKey: myClosureKey, queryFn: () => gateway.listMyAccountClosures(), retry: false });

  const done = async (text: PortalMessage): Promise<void> => {
    setOutcome({ tone: "done", text });
    await queryClient.invalidateQueries({ queryKey: ["workspace-lifecycle"] });
    await queryClient.invalidateQueries({ queryKey: ["account-closure"] });
    await queryClient.invalidateQueries({ queryKey: ["workspace"] });
  };
  const problem = (raised: unknown, otherwise: PortalMessage): void => {
    setOutcome({ tone: "problem", get text() { return apiProblemText(raised, resolvePortalMessage(otherwise)); } });
  };

  const page = (children: ReactNode): ReactNode => (
    <OrganizationPage
      tab="archive-close"

      title={portalText("core", "account_admin.archive_restore_close_f4f16f")}
      lede={portalText("core", "account_admin.archiving_stops_a_workspace_from_taking_new_work_and_keeps_its_hi_92b86c")}
      outcome={outcome}
    >
      {children}
    </OrganizationPage>
  );

  if (open.loading) return page(<BlockNote>{portalText("core", "account_admin.loading_the_organization_c14ce2")}</BlockNote>);
  if (accountId === null) {
    return page(
      <>
        <BlockNote>{portalText("core", "account_admin.choose_an_organization_first_066988")}</BlockNote>
        <MyClosures closures={mine.data ?? []} onDone={done} onProblem={problem} />
      </>,
    );
  }

  const rows = overview.data ?? [];
  const subject = preflightWorkspaceOf(rows, props.search.workspace);
  const archived = rows.filter((one) => one.state === "archived");

  return page(
    <>
      {overview.isPending && overview.fetchStatus !== "idle" ? <BlockNote>{portalText("core", "account_admin.loading_the_workspaces_779839")}</BlockNote> : null}
      {overview.isError ? (
        <BlockNote>
          {overview.error instanceof ApiError && (overview.error.status === 403 || overview.error.status === 404)
            ? portalText("core", "account_admin.your_role_does_not_show_this_organization_s_workspaces_079386")
            : apiProblemText(overview.error, portalText("core", "account_admin.the_workspaces_could_not_be_loaded_b842bc"))}
        </BlockNote>
      ) : null}
      {overview.data === undefined ? null : (
        <>
          <ArchiveBlock
            rows={rows}
            subject={subject}
            canManage={canManage}
            onPick={(workspaceId) => {
              props.onSearch({ workspace: workspaceId });
            }}
            onDone={done}
            onProblem={problem}
          />
          <ArchivedBlock archived={archived} canManage={canManage} onDone={done} onProblem={problem} />
        </>
      )}
      <Block label={portalText("core", "account_admin.close_the_organization_e79173")}>
        <CloseBlock accountId={accountId} canClose={canClose} onDone={done} onProblem={problem} />
      </Block>
      <MyClosures closures={mine.data ?? []} onDone={done} onProblem={problem} />
    </>,
  );
}

// ── Before archiving … ────────────────────────────────────────────────────────────────────────────────────

function ArchiveBlock(props: {
  readonly rows: readonly WorkspaceLifecycleRowView[];
  readonly subject: WorkspaceLifecycleRowView | null;
  readonly canManage: boolean;
  readonly onPick: (workspaceId: string) => void;
  readonly onDone: (text: PortalMessage) => Promise<void>;
  readonly onProblem: (raised: unknown, otherwise: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const subject = props.subject;
  // plan-014 review I-16 / I-19: "Archive when calls finish" opens the confirmation (`ArchivePreflightDialog`), which
  // mints its Idempotency-Key once per opening and is keyed by the workspace — so a key never follows the person to
  // another workspace, and nothing is archived (API keys revoked, campaigns paused) on one click.
  const [confirming, setConfirming] = useState(false);
  const preflight = useQuery({
    queryKey: archivePreflightKey(subject?.workspaceId ?? "none"),
    queryFn: () => gateway.readArchivePreflight(subject?.workspaceId ?? ""),
    enabled: subject !== null && props.canManage,
    retry: false,
  });
  const cancel = useMutation({
    mutationFn: (operationId: string) => gateway.cancelWorkspaceArchive(subject?.workspaceId ?? "", operationId),
    onSuccess: async () => {
      await props.onDone(() => (portalText("core", "account_admin.archiving_cancelled_numbers_and_widgets_are_connected_again_revok_2654b2")));
    },
    onError: (raised: unknown) => {
      props.onProblem(raised, () => (portalText("core", "account_admin.archiving_could_not_be_cancelled_914778")));
    },
  });
  if (subject === null) {
    return (
      <Block title={portalText("core", "account_admin.archive_a_workspace_8dad3e")}>
        <BlockNote>{portalText("core", "account_admin.every_workspace_is_archived_884cba")}</BlockNote>
      </Block>
    );
  }
  const openOperation = preflight.data?.openOperation ?? null;
  const choices = props.rows.filter((one) => one.state !== "archived");
  const onlyActive = choices.filter((one) => one.state === "active").length === 1 && subject.state === "active";
  const buttons = !props.canManage ? undefined : (
    <>
      {choices.length > 1 ? (
        <label className="lc-pick">
          <span className="wt-visually-hidden">{portalText("core", "account_admin.workspace_4ca0a7")}</span>
          <Select
            value={subject.workspaceId}
            onChange={(event) => {
              props.onPick(event.target.value);
            }}
          >
            {choices.map((one) => (
              <option key={one.workspaceId} value={one.workspaceId}>
                {one.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      {openOperation === null ? (
        <Button
          variant="primary"
          disabled={preflight.data === undefined || onlyActive}
          title={onlyActive ? portalText("core", "account_admin.an_organization_keeps_at_least_one_active_workspace_3cfd57") : undefined}
          onClick={() => {
            setConfirming(true);
          }}
        >{portalText("core", "account_admin.archive_when_calls_finish_156f6a")}</Button>
      ) : (
        <Button
          variant="secondary"
          disabled={cancel.isPending}
          onClick={() => {
            cancel.mutate(openOperation.operationId);
          }}
        >{portalText("core", "account_admin.cancel_archiving_78dc29")}</Button>
      )}
    </>
  );
  return (
    <Block
      title={portalText("core", "account_admin.before_archiving_c10e91", { value0: subject.name })}
      dek={
        openOperation === null
          ? portalText("core", "workspaces.what_will_happen_item_by_item_nothing_is_archived_until_you_confi_96b95b")
          : portalText("core", "account_admin.draining_no_new_calls_or_campaign_attempts_the_workspace_archives_7a3d35")
      }
      buttons={buttons}
      label={portalText("core", "account_admin.archive_preflight_66686c")}
    >
      {!props.canManage ? <BlockNote>{portalText("core", "account_admin.only_the_owner_or_an_admin_archives_workspaces_5dc800")}</BlockNote> : null}
      {preflight.isError ? <BlockNote>{apiProblemText(preflight.error, portalText("core", "account_admin.what_archiving_would_do_could_not_be_read_04da19"))}</BlockNote> : null}
      {preflight.data === undefined ? null : <ArchivePreflightTable item={openOperation === null ? preflight.data.item : openOperation.preflight} />}
      {onlyActive ? <BlockNote>{portalText("core", "account_admin.this_is_the_organization_s_only_active_workspace_so_it_cannot_be_92a750")}</BlockNote> : null}
      {confirming ? (
        <ArchivePreflightDialog
          key={subject.workspaceId}
          open
          workspaceId={subject.workspaceId}
          onClose={() => {
            setConfirming(false);
          }}
          onDone={(text) => {
            void props.onDone(text);
          }}
        />
      ) : null}
    </Block>
  );
}

// ── Archived ──────────────────────────────────────────────────────────────────────────────────────────────

function ArchivedBlock(props: {
  readonly archived: readonly WorkspaceLifecycleRowView[];
  readonly canManage: boolean;
  readonly onDone: (text: PortalMessage) => Promise<void>;
  readonly onProblem: (raised: unknown, otherwise: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const restore = useMutation({
    mutationFn: (workspaceId: string) => gateway.restoreWorkspace(workspaceId),
    onSuccess: async () => {
      await props.onDone(() => (portalText("core", "account_admin.restored_reconnect_a_number_and_start_campaigns_yourself_when_rea_07c1d3")));
    },
    onError: (raised: unknown) => {
      props.onProblem(raised, () => (portalText("core", "account_admin.that_workspace_could_not_be_restored_32758a")));
    },
  });
  return (
    <Block title={portalText("core", "account_admin.archived_eddc81")}>
      {props.archived.length === 0 ? (
        <BlockNote>{portalText("core", "account_admin.no_workspace_is_archived_398e95")}</BlockNote>
      ) : (
        <ItemColumn label={portalText("core", "account_admin.archived_workspaces_3378a5")}>
          {props.archived.map((one) => (
            <ItemRow
              key={one.workspaceId}
              glyph={GLYPH.archive}
              tone="neutral"
              title={one.name}
              tag={portalText("core", "account_admin.archived_eddc81")}
              dek={portalText("core", "account_admin.restoring_checks_sign_in_rules_funding_and_plan_limits_again_camp_3b97b7")}
              {...(one.archivedAt === null ? {} : { meta: portalText("core", "account_admin.since_c384ca", { value0: shortDateText(one.archivedAt) }) })}
              actions={
                props.canManage ? (
                  <Button
                    variant="secondary"
                    disabled={restore.isPending}
                    onClick={() => {
                      restore.mutate(one.workspaceId);
                    }}
                  >{portalText("core", "account_admin.restore_3cbe6d")}</Button>
                ) : undefined
              }
            />
          ))}
        </ItemColumn>
      )}
    </Block>
  );
}

// ── Close the organization ────────────────────────────────────────────────────────────────────────────────

function CloseBlock(props: {
  readonly accountId: string;
  readonly canClose: boolean;
  readonly onDone: (text: PortalMessage) => Promise<void>;
  readonly onProblem: (raised: unknown, otherwise: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const closedNotice = useClosedOrganizationNotice();
  const context = useQuery(authContextQuery(gateway));
  const accountName = context.data === undefined || context.data === null || context.data.kind === "selection_required" ? null : context.data.accountName;
  const [checked, setChecked] = useState(false);
  const [closing, setClosing] = useState(false);
  // 2026-10-15: while access is ending the closure is read every few seconds, so the page follows it to its end.
  const preflight = useQuery({
    queryKey: closurePreflightKey(props.accountId),
    queryFn: () => gateway.readClosurePreflight(props.accountId),
    enabled: props.canClose,
    retry: false,
    refetchInterval: (query) => {
      const latest = query.state.data?.latestClosure ?? null;
      return query.state.status !== "error" && latest !== null && closureEndingAccess(latest.state) ? CLOSURE_STATUS_EVERY_MS : false;
    },
  });
  // The closure this page saw ending access, kept so its end can be said once the organization can no longer be read.
  const [followed, setFollowed] = useState<{ readonly closure: AccountClosureView; readonly contentPurgeAfterDay: number } | null>(null);
  const seen = preflight.data?.latestClosure ?? null;
  if (
    seen !== null &&
    closureEndingAccess(seen.state) &&
    (followed === null || followed.closure.operationId !== seen.operationId || followed.closure.state !== seen.state)
  ) {
    setFollowed({ closure: seen, contentPurgeAfterDay: preflight.data?.retention.contentPurgeAfterDay ?? 0 });
  }
  const accessEnded =
    followed !== null && ((seen !== null && seen.operationId === followed.closure.operationId && closureAccessEnded(seen.state)) || (preflight.isError && accessEndedBy(preflight.error)));
  // Access ended: the organization menu's "Organizations you closed" is read again (it lists the closure from now on),
  // and so is the sign-in, which the API moves to another organization the person belongs to — or to the list of
  // organizations — where the move says that this one is closed. Never a loop: this runs once per closure.
  const announced = useRef<string | null>(null);
  const ended = accessEnded ? followed : null;
  useEffect(() => {
    if (ended === null || announced.current === ended.closure.operationId) return;
    announced.current = ended.closure.operationId;
    if (accountName !== null) {
      closedNotice.expect({ kind: "organization_closed", accountName, reopenUntil: reopenUntilOf(ended.closure, ended.contentPurgeAfterDay) });
    }
    void queryClient.invalidateQueries({ queryKey: myClosureKey });
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY.authSession });
  }, [ended, accountName, closedNotice, queryClient]);
  const withdraw = useMutation({
    mutationFn: (operationId: string) => gateway.withdrawAccountClosure(props.accountId, operationId),
    onSuccess: async (closure) => {
      await props.onDone(() => closureStatusText(closure));
    },
    onError: (raised: unknown) => {
      props.onProblem(raised, () => (portalText("core", "account_admin.closing_could_not_be_withdrawn_0b1313")));
    },
  });
  if (ended !== null) {
    return (
      <Notice
        tone="caution"
        glyph={GLYPH.warningOctagon}
        title={accountName === null ? portalText("core", "account_admin.closing_the_organization_eef548") : portalText("core", "account_admin.value_is_closing_3a91c7", { value0: accountName })}
        text={portalText("core", "account_admin.access_to_this_organization_has_ended_taking_you_to_another_orga_c2d4f8")}
      />
    );
  }
  const latest = preflight.data?.latestClosure ?? null;
  if (closureOpen(latest)) {
    const endingAccess = closureEndingAccess(latest.state);
    return (
      <>
        <Notice
          tone="caution"
          glyph={GLYPH.warningOctagon}
          title={
            endingAccess && accountName !== null
              ? portalText("core", "account_admin.value_is_closing_3a91c7", { value0: accountName })
              : portalText("core", "account_admin.closing_the_organization_eef548")
          }
          text={endingAccess ? closingExplanationText(latest, preflight.data?.retention.contentPurgeAfterDay ?? 0) : closureStatusText(latest)}
          actions={
            closureWithdrawable(latest.state) ? (
              <Button
                variant="secondary"
                disabled={withdraw.isPending}
                onClick={() => {
                  withdraw.mutate(latest.operationId);
                }}
              >{portalText("core", "account_admin.withdraw_47e564")}</Button>
            ) : undefined
          }
        />
        {endingAccess ? (
          <p className="c-note lc-check" role="status">
            {closureStatusText(latest)}
          </p>
        ) : null}
      </>
    );
  }
  const blocker = preflight.data?.blocker ?? [];
  return (
    <>
      <Notice
        tone="caution"
        glyph={GLYPH.warningOctagon}
        title={portalText("core", "account_admin.close_the_organization_e79173")}
        text={portalText("core", "account_admin.only_the_owner_can_close_it_with_their_password_calls_drain_plans_aa7b9e")}
        actions={
          props.canClose ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setChecked(true);
                  void preflight.refetch();
                }}
              >{portalText("core", "account_admin.check_what_blocks_closing_157505")}</Button>
              {checked && preflight.data !== undefined && blocker.length === 0 ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setClosing(true);
                  }}
                >{portalText("core", "account_admin.close_the_organization_e79173")}</Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {!props.canClose ? <BlockNote>{portalText("core", "account_admin.only_the_owner_can_close_the_organization_22519d")}</BlockNote> : null}
      {checked && preflight.isError ? <BlockNote>{apiProblemText(preflight.error, portalText("core", "account_admin.what_blocks_closing_could_not_be_checked_a9cadd"))}</BlockNote> : null}
      {checked && preflight.data !== undefined ? (
        <p className={blocker.length === 0 ? "c-note lc-check" : "c-note c-note-critical lc-check"} role="status">
          {blockerSummaryText(blocker)}
        </p>
      ) : null}
      {preflight.data === undefined || !closing ? null : (
        <CloseDialog
          open={closing}
          accountId={props.accountId}
          preflight={preflight.data}
          onClose={() => {
            setClosing(false);
          }}
          onDone={props.onDone}
          onProblem={props.onProblem}
        />
      )}
    </>
  );
}

function CloseDialog(props: {
  readonly open: boolean;
  readonly accountId: string;
  readonly preflight: ClosurePreflightView;
  readonly onClose: () => void;
  readonly onDone: (text: PortalMessage) => Promise<void>;
  readonly onProblem: (raised: unknown, otherwise: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  // One Idempotency-Key per request that can run: a double click or a retry after a lost answer is recognised, but a
  // refusal that asked for a confirmation ran nothing, so the request sent after the confirmation takes a new key
  // (2026-10-15, owner report: the API replayed the stored `step_up_required` to every retry under the old key, and
  // the password was asked again and again while nothing could ever start).
  const key = useRef<string>(crypto.randomUUID());
  const refundable = props.preflight.refundableMinor;
  const [choice, setChoice] = useState<"refund" | "waive">("refund");
  const [waiver, setWaiver] = useState("");
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [blocked, setBlocked] = useState<readonly ClosureBlockerView[] | null>(null);
  const [confirmationLost, setConfirmationLost] = useState(false);
  const waiverReady = waiver.trim().length >= 10 && waiver.trim().length <= 1000;

  const start = async (): Promise<void> => {
    const started = await gateway.startAccountClosure(
      props.accountId,
      refundable === 0 ? { refundChoice: "none", refundWaiver: null } : { refundChoice: choice, refundWaiver: choice === "waive" ? waiver.trim() : null },
      key.current,
    );
    if (started.kind === "blocked") {
      setBlocked(started.blocker);
      return;
    }
    props.onClose();
    await props.onDone(() => closureStatusText(started.closure));
  };
  /** The request again, once the person confirmed it is them. Refused for a confirmation again, it says so — never a loop. */
  const afterConfirmation = async (): Promise<void> => {
    try {
      await start();
    } catch (raised: unknown) {
      if (!isStepUpRequired(raised)) throw raised;
      key.current = crypto.randomUUID();
      setConfirmationLost(true);
    }
  };
  const close = useMutation({
    mutationFn: start,
    onError: (raised: unknown) => {
      if (isStepUpRequired(raised)) {
        key.current = crypto.randomUUID();
        setPending({ retry: afterConfirmation });
        return;
      }
      props.onProblem(raised, () => (portalText("core", "account_admin.closing_the_organization_could_not_be_started_482c35")));
    },
  });
  const retention = props.preflight.retention;
  return (
    <>
      <Dialog
        open={props.open && pending === null}
        title={portalText("core", "account_admin.close_the_organization_e79173")}
        onClose={props.onClose}
        actions={
          <>
            <Button variant="secondary" onClick={props.onClose}>{portalText("core", "account_admin.keep_it_open_a65b4c")}</Button>
            <Button
              variant="primary"
              disabled={close.isPending || (refundable > 0 && choice === "waive" && !waiverReady)}
              onClick={() => {
                setBlocked(null);
                setConfirmationLost(false);
                close.mutate();
              }}
            >
              {close.isPending ? portalText("core", "account_admin.closing_0f0a55") : portalText("core", "account_admin.close_the_organization_e79173")}
            </Button>
          </>
        }
      >
        <div className="op-dialog-form">
          <p className="op-dialog-text">{portalText("core", "account_admin.new_calls_and_purchases_stop_at_once_conversations_in_progress_fi_fe5c36")}{" "}{String(retention.contentPurgeAfterDay)}{" "}{portalText("core", "account_admin.days_after_that_until_then_you_can_undo_it_invoices_and_the_ledge_b70a85")}{" "}{String(retention.financialKeepYear)}{" "}{portalText("core", "account_admin.years_7c361f")}</p>
          {refundable === 0 ? (
            <p className="op-dialog-text">{portalText("core", "account_admin.no_purchased_credit_is_left_unused_so_there_is_nothing_to_refund_5ab47a")}</p>
          ) : (
            <fieldset className="lc-refund">
              <legend className="lc-legend">{portalText("core", "account_admin.unused_purchased_credit_f1469b")}{" "}{euroText(refundable)}</legend>
              <Radio
                name="lc-refund"
                label={portalText("core", "account_admin.refund_it_to_me_a_wetalk_finance_person_approves_each_refund_ee3ef4")}
                checked={choice === "refund"}
                onChange={() => {
                  setChoice("refund");
                }}
              />
              <Radio
                name="lc-refund"
                label={portalText("core", "account_admin.i_give_it_up_dae0fd")}
                checked={choice === "waive"}
                onChange={() => {
                  setChoice("waive");
                }}
              />
              {choice === "waive" ? (
                <Field label={portalText("core", "account_admin.say_so_in_your_own_words_79205f")} description={portalText("core", "account_admin.kept_with_the_closure_at_least_10_characters_76986d")}>
                  {(control) => (
                    <Textarea
                      {...control}
                      rows={3}
                      value={waiver}
                      onChange={(event) => {
                        setWaiver(event.currentTarget.value);
                      }}
                    />
                  )}
                </Field>
              ) : null}
              <p className="op-dialog-text">{portalText("core", "account_admin.trial_and_promotional_credit_are_never_refunded_d4a1bc")}</p>
            </fieldset>
          )}
          {blocked === null ? null : (
            <p className="c-note c-note-critical" role="alert">
              {blockerSummaryText(blocked)}
            </p>
          )}
          {confirmationLost ? (
            <p className="c-note c-note-critical" role="alert">
              {portalText("core", "account_admin.your_confirmation_was_not_accepted_for_this_request_4e7d10")}
            </p>
          ) : null}
        </div>
      </Dialog>
      <ConfirmIdentityDialog
        pending={pending}
        preferred="password"
        returnPath="/organization/archive-close"
        onDone={() => {
          setPending(null);
        }}
        onProblem={(text) => {
          props.onProblem(null, text);
        }}
      />
    </>
  );
}

// ── Organizations you closed ──────────────────────────────────────────────────────────────────────────────

function MyClosures(props: {
  readonly closures: readonly { readonly accountId: string; readonly accountName: string; readonly operationId: string; readonly graceUntil: string }[];
  readonly onDone: (text: PortalMessage) => Promise<void>;
  readonly onProblem: (raised: unknown, otherwise: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const undo = useMutation({
    mutationFn: (one: { readonly accountId: string; readonly operationId: string }) => gateway.undoAccountClosure(one.accountId, one.operationId),
    onSuccess: async (closure) => {
      await props.onDone(() => closureStatusText(closure));
    },
    onError: (raised: unknown) => {
      props.onProblem(raised, () => (portalText("core", "account_admin.closing_could_not_be_undone_c6d287")));
    },
  });
  if (props.closures.length === 0) return null;
  return (
    <Block title={portalText("core", "account_admin.organizations_you_closed_c49530")} dek={portalText("core", "account_admin.you_can_undo_a_closure_until_its_content_is_deleted_20f188")}>
      <ItemColumn label={portalText("core", "account_admin.organizations_you_closed_c49530")}>
        {props.closures.map((one) => (
          <ItemRow
            key={one.operationId}
            glyph={GLYPH.archive}
            tone="neutral"
            title={one.accountName}
            tag={portalText("core", "account_admin.closed_88d86b")}
            dek={portalText("core", "account_admin.access_has_ended_its_content_is_kept_until_df3282", { value0: LONG_DATE().format(new Date(one.graceUntil)) })}
            actions={
              <Button
                variant="secondary"
                disabled={undo.isPending}
                onClick={() => {
                  undo.mutate(one);
                }}
              >{portalText("core", "account_admin.undo_39fc72")}</Button>
            }
          />
        ))}
      </ItemColumn>
    </Block>
  );
}
